import React, { useState } from 'react';
import { EditableOutlined } from '@duxton-web/icons';
import {
  Button,
  InputNumber,
  Modal,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import { RolloutTarget } from '@/lib/PlannerDTOs';

interface OwnProps {
  rolloutTargetIndex: number;
  rolloutTarget: RolloutTarget;
  onChange: (fromIndex: number, toIndex: number) => void;
  disabled: boolean;
  maxPosition: number;
  isReordered: boolean;
}

export default function Editor({
  rolloutTargetIndex,
  onChange,
  disabled,
  maxPosition,
}: OwnProps) {
  const [openEditModal, setOpenEditModal] = useState(false);
  // new rollout target index to be saved
  const [newRolloutTargetIndex, setNewRolloutTargetIndex] =
    useState<number>(null);
  const [error, setError] = useState<string>('');

  function closeEditModal(e): void {
    e.stopPropagation();

    setOpenEditModal(false);
    setNewRolloutTargetIndex(null);
    setError('');
  }

  function saveRolloutIndex(e): void {
    e.stopPropagation();

    setOpenEditModal(false);
    onChange(rolloutTargetIndex, newRolloutTargetIndex);
  }

  function handleChange(val) {
    if (!isNaN(val)) {
      setNewRolloutTargetIndex(val);

      if (val % 1 !== 0) {
        setError('Please enter a valid whole number');
        return;
      }

      if (val < 1 || val > maxPosition) {
        if (maxPosition !== 1) {
          setError(`Please enter a value between 1 to ${maxPosition}`);
          return;
        }

        setError('Value can only be 1');
        return;
      }

      setError('');
    }
  }

  if (disabled) return null;

  return (
    <>
      <Tooltip title="Click here to reorder the rollout target">
        <Button
          size="small"
          type="secondary"
          shape="circle"
          onClick={(e) => {
            e.stopPropagation();

            setOpenEditModal(true);
            setNewRolloutTargetIndex(rolloutTargetIndex);
            setError('');
          }}
          icon={<EditableOutlined />}
        />
      </Tooltip>

      <div
        data-test-id="rollout-editor-position-editor-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <Modal
          open={openEditModal}
          title="Order Position"
          okText="Save"
          onCancel={closeEditModal}
          cancelButtonProps={{
            size: 'small',
          }}
          okButtonProps={{
            disabled:
              Boolean(error) || newRolloutTargetIndex === rolloutTargetIndex,
            size: 'small',
            type: 'primary',
          }}
          onOk={saveRolloutIndex}
          centered
        >
          <InputNumber
            id="rollout-target-order-position-input"
            data-testid="input-number"
            style={{ width: '100%' }}
            type="number"
            status={error ? 'error' : ''}
            onChange={handleChange}
            value={newRolloutTargetIndex}
          />
          {error && (
            <Typography.Text type="danger" style={{ marginTop: 4 }}>
              {error}
            </Typography.Text>
          )}
        </Modal>
      </div>
    </>
  );
}
